import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Search, Calendar as CalendarIcon, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/imageUrl';

export const TrainingCalendarAdmin = () => {
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modeFilter, setModeFilter] = useState('All');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category_id: '',
    description: '',
    banner_heading: '',
    banner_description: '',
    banner_thumbnail_url: null,
    date: '',
    duration: '',
    mode: 'Classroom',
    location: '',
    price: '',
    seats_available: '',
    instructor: '',
    poster_url: null,
    is_paid: true,
    registration_link: '',
    use_internal_form: true,
    is_featured: false,
    related_pages: []
  });
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingBannerThumbnail, setUploadingBannerThumbnail] = useState(false);
  const { toast } = useToast();

  const modes = ['All', 'Classroom', 'Online', 'Hybrid', 'On-Site'];

  // Available course pages where trainings can appear
  const AVAILABLE_PAGES = [
    // ISO 9001
    { slug: 'iso-9001', name: 'ISO 9001 (QMS Overview)', category: 'ISO 9001' },
    { slug: 'iso-9001-awareness', name: 'ISO 9001 Awareness', category: 'ISO 9001' },
    { slug: 'iso-9001-internal-auditor', name: 'ISO 9001 Internal Auditor', category: 'ISO 9001' },
    { slug: 'iso-9001-lead-auditor', name: 'ISO 9001 Lead Auditor', category: 'ISO 9001' },

    // ISO 14001
    { slug: 'iso-14001', name: 'ISO 14001 (EMS Overview)', category: 'ISO 14001' },
    { slug: 'iso-14001-awareness', name: 'ISO 14001 Awareness', category: 'ISO 14001' },
    { slug: 'iso-14001-internal-auditor', name: 'ISO 14001 Internal Auditor', category: 'ISO 14001' },
    { slug: 'iso-14001-lead-auditor', name: 'ISO 14001 Lead Auditor', category: 'ISO 14001' },

    // ISO 27001
    { slug: 'iso-27001', name: 'ISO 27001 (ISMS Overview)', category: 'ISO 27001' },
    { slug: 'iso-27001-awareness', name: 'ISO 27001 Awareness', category: 'ISO 27001' },
    { slug: 'iso-27001-internal-auditor', name: 'ISO 27001 Internal Auditor', category: 'ISO 27001' },
    { slug: 'iso-27001-lead-auditor', name: 'ISO 27001 Lead Auditor', category: 'ISO 27001' },

    // ISO 45001
    { slug: 'iso-45001', name: 'ISO 45001 (OH&S Overview)', category: 'ISO 45001' },
    { slug: 'iso-45001-awareness', name: 'ISO 45001 Awareness', category: 'ISO 45001' },
    { slug: 'iso-45001-internal-auditor', name: 'ISO 45001 Internal Auditor', category: 'ISO 45001' },
    { slug: 'iso-45001-lead-auditor', name: 'ISO 45001 Lead Auditor', category: 'ISO 45001' },

    // ISO 13485
    { slug: 'iso-13485', name: 'ISO 13485 (Medical Devices)', category: 'ISO 13485' },
    { slug: 'iso-13485-awareness', name: 'ISO 13485 Awareness', category: 'ISO 13485' },
    { slug: 'iso-13485-internal-auditor', name: 'ISO 13485 Internal Auditor', category: 'ISO 13485' },
    { slug: 'iso-13485-lead-auditor', name: 'ISO 13485 Lead Auditor', category: 'ISO 13485' },

    // ISO 22000
    { slug: 'iso-22000', name: 'ISO 22000 (FSMS Overview)', category: 'ISO 22000' },
    { slug: 'iso-22000-awareness', name: 'ISO 22000 Awareness', category: 'ISO 22000' },
    { slug: 'iso-22000-internal-auditor', name: 'ISO 22000 Internal Auditor', category: 'ISO 22000' },

    // ISO 50001
    { slug: 'iso-50001', name: 'ISO 50001 (EnMS Overview)', category: 'ISO 50001' },
    { slug: 'iso-50001-awareness', name: 'ISO 50001 Awareness', category: 'ISO 50001' },
    { slug: 'iso-50001-internal-auditor', name: 'ISO 50001 Internal Auditor', category: 'ISO 50001' },
    { slug: 'iso-50001-lead-auditor', name: 'ISO 50001 Lead Auditor', category: 'ISO 50001' },

    // ISO 21001
    { slug: 'iso-21001', name: 'ISO 21001 (EOMS Overview)', category: 'ISO 21001' },
    { slug: 'iso-21001-awareness', name: 'ISO 21001 Awareness', category: 'ISO 21001' },
    { slug: 'iso-21001-internal-auditor', name: 'ISO 21001 Internal Auditor', category: 'ISO 21001' },

    // IATF 16949
    { slug: 'iatf-16949', name: 'IATF 16949 (Automotive)', category: 'IATF 16949' },
    { slug: 'iatf-16949-awareness', name: 'IATF 16949 Awareness', category: 'IATF 16949' },
    { slug: 'iatf-16949-internal-auditor', name: 'IATF 16949 Internal Auditor', category: 'IATF 16949' },

    // Automotive Quality Tools
    { slug: 'fmea-training', name: 'FMEA Training', category: 'Automotive Quality' },
    { slug: 'core-tools-training', name: 'Core Tools Training (APQP, PPAP, MSA, SPC, FMEA)', category: 'Automotive Quality' },
  ];

  useEffect(() => {
    fetchTrainings();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterTrainings();
  }, [trainings, searchQuery, modeFilter]);

  const fetchTrainings = async () => {
    try {
      const response = await api.get('/admin/training-calendar');
      setTrainings(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch trainings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/training-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const filterTrainings = () => {
    let filtered = trainings;

    if (searchQuery) {
      filtered = filtered.filter(training =>
        training.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        training.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (modeFilter !== 'All') {
      filtered = filtered.filter(training => training.mode === modeFilter);
    }

    setFilteredTrainings(filtered);
  };

  const handlePosterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Error',
        description: 'File size must be less than 10MB',
        variant: 'destructive'
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Error',
        description: 'Please upload an image file',
        variant: 'destructive'
      });
      return;
    }

    setUploadingPoster(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/admin/upload/poster', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, poster_url: response.data.url });
      toast({
        title: 'Success',
        description: 'Poster uploaded successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload poster',
        variant: 'destructive'
      });
    } finally {
      setUploadingPoster(false);
    }
  };

  const handleBannerThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Error',
        description: 'File size must be less than 10MB',
        variant: 'destructive'
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Error',
        description: 'Please upload an image file',
        variant: 'destructive'
      });
      return;
    }

    setUploadingBannerThumbnail(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/admin/upload/poster', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, banner_thumbnail_url: response.data.url });
      toast({
        title: 'Success',
        description: 'Banner thumbnail uploaded successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload banner thumbnail',
        variant: 'destructive'
      });
    } finally {
      setUploadingBannerThumbnail(false);
    }
  };

  const handleOpenDialog = (training = null) => {
    if (training) {
      setEditingTraining(training);
      setFormData({
        title: training.title,
        category_id: training.category_id || '',
        description: training.description || '',
        banner_heading: training.banner_heading || '',
        banner_description: training.banner_description || '',
        banner_thumbnail_url: training.banner_thumbnail_url || null,
        date: training.date ? new Date(training.date).toISOString().slice(0, 16) : '',
        duration: training.duration,
        mode: training.mode,
        location: training.location,
        price: training.price,
        seats_available: training.seats_available,
        instructor: training.instructor || '',
        poster_url: training.poster_url || null,
        is_paid: training.is_paid !== undefined ? training.is_paid : true,
        registration_link: training.registration_link || '',
        use_internal_form: training.use_internal_form !== undefined ? training.use_internal_form : true,
        is_featured: training.is_featured || false,
        related_pages: training.related_pages || []
      });
    } else {
      setEditingTraining(null);
      setFormData({
        title: '',
        category_id: '',
        description: '',
        date: '',
        duration: '',
        mode: 'Classroom',
        location: '',
        price: '',
        seats_available: '',
        instructor: '',
        poster_url: null,
        is_paid: true,
        registration_link: '',
        use_internal_form: true,
        is_featured: false,
        related_pages: []
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        seats_available: parseInt(formData.seats_available),
        category_id: formData.category_id ? parseInt(formData.category_id) : null
      };

      if (editingTraining) {
        await api.put(`/admin/training-calendar/${editingTraining.id}`, submitData);
        toast({
          title: 'Success',
          description: 'Training updated successfully'
        });
      } else {
        await api.post('/admin/training-calendar', submitData);
        toast({
          title: 'Success',
          description: 'Training created successfully'
        });
      }
      
      setIsDialogOpen(false);
      fetchTrainings();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save training',
        variant: 'destructive'
      });
    }
  };

  const toggleRelatedPage = (pageSlug) => {
    setFormData(prev => {
      const isSelected = prev.related_pages.includes(pageSlug);
      return {
        ...prev,
        related_pages: isSelected
          ? prev.related_pages.filter(slug => slug !== pageSlug)
          : [...prev.related_pages, pageSlug]
      };
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this training?')) return;
    
    try {
      await api.delete(`/admin/training-calendar/${id}`);
      toast({
        title: 'Success',
        description: 'Training deleted successfully'
      });
      fetchTrainings();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete training',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Training Calendar</h2>
          <p className="text-gray-600 mt-1">Manage all training programs and schedules</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add New Training
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search trainings by title or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {modes.map(mode => (
                <Button
                  key={mode}
                  onClick={() => setModeFilter(mode)}
                  variant={modeFilter === mode ? 'default' : 'outline'}
                  className={modeFilter === mode ? 'bg-blue-600' : ''}
                >
                  {mode}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trainings List */}
      <div className="grid gap-6">
        {filteredTrainings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CalendarIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">No trainings found</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                Add First Training
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredTrainings.map((training) => (
            <motion.div
              key={training.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{training.title}</h3>
                        {training.is_featured && (
                          <Badge className="bg-yellow-100 text-yellow-800">Featured</Badge>
                        )}
                      </div>
                      
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-600">Date</p>
                          <p className="font-semibold text-gray-900">
                            {new Date(training.date).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Duration</p>
                          <p className="font-semibold text-gray-900">{training.duration}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Mode</p>
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                            {training.mode}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Location</p>
                          <p className="font-semibold text-gray-900">{training.location}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Price</p>
                          <p className="font-semibold text-gray-900">{training.price}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Available Seats</p>
                          <p className="font-semibold text-green-600">{training.seats_available}</p>
                        </div>
                        {training.instructor && (
                          <div>
                            <p className="text-sm text-gray-600">Instructor</p>
                            <p className="font-semibold text-gray-900">{training.instructor}</p>
                          </div>
                        )}
                      </div>
                      
                      {training.description && (
                        <p className="text-gray-600 mt-4 line-clamp-2">{training.description}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleOpenDialog(training)}
                        variant="outline"
                        size="icon"
                        className="border-blue-600 text-blue-600 hover:bg-blue-50"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(training.id)}
                        variant="outline"
                        size="icon"
                        className="border-red-600 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTraining ? 'Edit Training' : 'Add New Training'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., ISO 9001:2015 Internal Auditor Training"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Category</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.title}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700">Date & Time *</label>
                <Input
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Duration *</label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  required
                  placeholder="e.g., 2 Days"
                />
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700">Mode *</label>
                <select
                  value={formData.mode}
                  onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                >
                  <option value="Classroom">Classroom</option>
                  <option value="Online">Online</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="On-Site">On-Site</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Location *</label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  placeholder="e.g., Chennai or Virtual"
                />
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700">Price *</label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  placeholder="e.g., ₹12,000"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Available Seats *</label>
                <Input
                  type="number"
                  value={formData.seats_available}
                  onChange={(e) => setFormData({ ...formData, seats_available: e.target.value })}
                  required
                  min="1"
                />
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700">Instructor</label>
                <Input
                  value={formData.instructor}
                  onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                  placeholder="Instructor name"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Poster Image</label>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handlePosterUpload}
                  className="cursor-pointer"
                />
                {formData.poster_url && (
                  <div className="relative w-full h-48 border border-gray-300 rounded-md overflow-hidden">
                    <img
                      src={getImageUrl(formData.poster_url)}
                      alt="Poster preview"
                      className="w-full h-full object-cover"
                    />
                    <Button
                      type="button"
                      onClick={() => setFormData({ ...formData, poster_url: null })}
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
                rows={3}
                placeholder="Training description..."
              />
              <p className="text-xs text-gray-500 mt-1">
                This appears in training lists and overviews
              </p>
            </div>

            {/* Banner/Hero Section Fields */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-md font-semibold text-gray-900 mb-3">Banner/Hero Section (Detail Page)</h3>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Banner Heading</label>
                  <Input
                    type="text"
                    value={formData.banner_heading}
                    onChange={(e) => setFormData({ ...formData, banner_heading: e.target.value })}
                    placeholder="e.g., ISO 21001 : 2018 LEAD AUDITOR TRAINING"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Custom heading for the detail page banner (leave empty to use title)
                  </p>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700">Banner Description</label>
                  <textarea
                    value={formData.banner_description}
                    onChange={(e) => setFormData({ ...formData, banner_description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
                    rows={3}
                    placeholder="e.g., Educational Organization Management System\nWebinar - (7pm - 10pm)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Custom description for the detail page banner (leave empty to use description)
                  </p>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700">Banner Thumbnail Image</label>
                  <div className="space-y-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerThumbnailUpload}
                      className="cursor-pointer"
                      disabled={uploadingBannerThumbnail}
                    />
                    {uploadingBannerThumbnail && (
                      <p className="text-sm text-blue-600">Uploading...</p>
                    )}
                    {formData.banner_thumbnail_url && (
                      <div className="relative w-full h-48 border border-gray-300 rounded-md overflow-hidden">
                        <img
                          src={getImageUrl(formData.banner_thumbnail_url)}
                          alt="Banner thumbnail preview"
                          className="w-full h-full object-cover"
                        />
                        <Button
                          type="button"
                          onClick={() => setFormData({ ...formData, banner_thumbnail_url: null })}
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Custom image for banner section (leave empty to use poster image)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Type */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Event Type</label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_paid"
                    checked={formData.is_paid === true}
                    onChange={() => setFormData({ ...formData, is_paid: true })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Paid Event</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_paid"
                    checked={formData.is_paid === false}
                    onChange={() => setFormData({ ...formData, is_paid: false })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Complimentary Event</span>
                </label>
              </div>
            </div>

            {/* Registration Form Toggle */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Registration Form</label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="use_internal_form"
                    checked={formData.use_internal_form === true}
                    onChange={() => setFormData({ ...formData, use_internal_form: true, registration_link: '' })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Use Internal Form</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="use_internal_form"
                    checked={formData.use_internal_form === false}
                    onChange={() => setFormData({ ...formData, use_internal_form: false })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Use External Link</span>
                </label>
              </div>
            </div>

            {/* External Registration Link */}
            {!formData.use_internal_form && (
              <div>
                <label className="text-sm font-semibold text-gray-700">External Registration Link *</label>
                <Input
                  type="url"
                  value={formData.registration_link}
                  onChange={(e) => setFormData({ ...formData, registration_link: e.target.value })}
                  placeholder="https://forms.google.com/..."
                  required={!formData.use_internal_form}
                />
                <p className="text-xs text-gray-500 mt-1">Provide the complete URL for external registration</p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_featured"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                className="w-4 h-4 text-blue-600"
              />
              <label htmlFor="is_featured" className="text-sm font-semibold text-gray-700">
                Featured Training
              </label>
            </div>

            {/* Related Pages Section */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-900 mb-1">
                    Display on Course Pages
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Select which course pages should show this training. For example, "ISO 9001 Internal Auditor" training should appear on both the ISO 9001 overview page and ISO 9001 Internal Auditor course page.
                  </p>
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                {/* Group by category */}
                {Object.entries(
                  AVAILABLE_PAGES.reduce((acc, page) => {
                    if (!acc[page.category]) acc[page.category] = [];
                    acc[page.category].push(page);
                    return acc;
                  }, {})
                ).map(([category, pages]) => (
                  <div key={category} className="mb-4 last:mb-0">
                    <h4 className="text-xs font-bold text-gray-700 mb-2 bg-white px-2 py-1 rounded">
                      {category}
                    </h4>
                    <div className="space-y-1 pl-2">
                      {pages.map((page) => (
                        <label
                          key={page.slug}
                          className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.related_pages.includes(page.slug)}
                            onChange={() => toggleRelatedPage(page.slug)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700">{page.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {formData.related_pages.length > 0 && (
                <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
                  <strong>Selected:</strong> {formData.related_pages.length} page(s) - This training will appear on: {formData.related_pages.join(', ')}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingTraining ? 'Update Training' : 'Create Training'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
